class ProductCarousel {
  constructor(options) {
    this.container = options.container;
    this.productHandle = options.productHandle;
    this.enableZoom = options.enableZoom || false;
    this.thumbnailsPosition = options.thumbnailsPosition || 'bottom';
    this.initialVariantId = options.initialVariantId || null;
    this.mediaItems = [];
    this.currentIndex = 0;
    this.isZoomed = false;
    this.zoomOverlay = null;
    this.isMobile = window.innerWidth <= 749;
    
    // Throttle resize/scroll for smoother experience
    this.throttledResize = this.throttle(this.handleResize.bind(this), 100);
    this.throttledScroll = this.throttle(this.handleMobileScroll.bind(this), 50);

    // Cache DOM elements for performance
    this.mediaList = this.container.querySelector('.product__media-list');
    this.thumbnailList = this.container.querySelector('.product__thumbnail-list');
    this.prevButton = this.container.querySelector('[data-action="prev"]');
    this.nextButton = this.container.querySelector('[data-action="next"]');
    this.zoomButton = this.container.querySelector('.product__zoom-button');

    if (!this.mediaList) return;

    // Main setup
    this.initializeMediaItems();
    this.setupEventListeners();
    if (this.enableZoom) {
      this.setupZoom();
    }
  }

  // Prevents frequent calls for better performance
  throttle(func, wait) {
    let timeout;
    let lastRan;
    return function executedFunction(...args) {
      if (!lastRan) {
        func(...args);
        lastRan = Date.now();
      } else {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          if ((Date.now() - lastRan) >= wait) {
            func(...args);
            lastRan = Date.now();
          }
        }, wait - (Date.now() - lastRan));
      }
    };
  }

  initializeMediaItems() {
    // Collect all visible product images
    const items = this.mediaList.querySelectorAll('.product__media-item:not(.is-filtered)');
    this.mediaItems = Array.from(items);
    
    if (this.mediaItems.length === 0) return;
    
    // Decide which image to show first
    let initialIndex = 0;
    
    // First, check for an initial variant ID
    if (this.initialVariantId) {
      // Try to find the media associated with this variant
      this.mediaItems.forEach((item, index) => {
        const mediaVariantIds = item.getAttribute('data-media-variant-ids');
        if (mediaVariantIds && mediaVariantIds.split(',').includes(String(this.initialVariantId))) {
          initialIndex = index;
        }
      });
    } else {
      // If no initial variant, check for the explicitly marked initial item
      const initialItem = this.mediaList.querySelector('.is-initial-selected');
      if (initialItem) {
        initialIndex = this.mediaItems.indexOf(initialItem);
      }
    }
    
    this.currentIndex = initialIndex;
    
    // Initialize arrow navigation buttons
    this.initializeArrowNavigation();
    
    // Set up mobile-specific styles if needed - use requestAnimationFrame for smooth rendering
    if (this.isMobile && this.mediaList) {
      requestAnimationFrame(() => {
        this.mediaList.style.display = 'flex';
        this.mediaList.style.overflowX = 'auto';
        this.mediaList.style.scrollSnapType = 'x mandatory';
        this.mediaList.style.WebkitOverflowScrolling = 'touch';
        
        this.mediaItems.forEach(item => {
          item.style.flex = '0 0 100%';
          item.style.width = '100%';
          item.style.scrollSnapAlign = 'center';
        });
      });
    }
    
    // Set initial state with optimized timing
    this.updateCarousel();
    
    // Set up event handlers for the thumbnails rendered by Liquid
    this.createThumbnails();
    
    // Single optimized visibility check
    requestAnimationFrame(() => {
      this.ensureImageVisibility();
      this.updateThumbnails();
      // Preload adjacent images on initial load
      this.preloadAdjacentImages();
    });
  }
  
  // image visibility function
  ensureImageVisibility() {
    const currentItem = this.mediaItems[this.currentIndex];
    if (!currentItem) return;
    
    const img = currentItem.querySelector('img');
    if (!img) return;

    // Simple display update
    img.style.opacity = '1';
    img.style.visibility = 'visible';
    img.style.display = 'block';
  }
  
  initializeArrowNavigation() {
    if (this.mediaItems.length <= 1) return;
    // Connect arrow navigation
    const prevButton = this.container.querySelector('.product__media-arrow--prev');
    const nextButton = this.container.querySelector('.product__media-arrow--next');
    if (prevButton) prevButton.addEventListener('click', this.goToPrev.bind(this));
    if (nextButton) nextButton.addEventListener('click', this.goToNext.bind(this));
  }

  updateCarousel() {
    // Animate carousel updates
    requestAnimationFrame(() => {
      // Check if we're on mobile
      const isMobile = this.isMobile;
      
      if (isMobile) {
        // For mobile: Show all items and arrange horizontally for scrolling
        this.mediaItems.forEach((item, index) => {
          item.classList.toggle('is-selected', index === this.currentIndex);
          // Keep all visible for scroll snap to work
          item.style.display = 'block';
          item.style.opacity = '1';
          item.style.visibility = 'visible';
        });
        
        // Scroll to current item using scrollLeft for precise snap alignment
        if (this.mediaItems[this.currentIndex] && this.mediaList) {
          const itemWidth = this.mediaList.offsetWidth;
          const targetScroll = this.currentIndex * itemWidth;
          this.mediaList.scrollTo({ 
            left: targetScroll, 
            behavior: 'smooth' 
          });
        }
      } else {
        // For desktop: Show only the current item
        this.mediaItems.forEach((item, index) => {
          if (index === this.currentIndex) {
            item.classList.add('is-selected');
            item.style.display = 'block';
            item.style.opacity = '1';
            item.style.visibility = 'visible';
          } else {
            item.classList.remove('is-selected');
            item.style.display = 'none';
            item.style.opacity = '0';
            item.style.visibility = 'hidden';
          }
        });
        
        // Ensure current image is visible
        const currentItem = this.mediaItems[this.currentIndex];
        if (currentItem) {
          const img = currentItem.querySelector('img');
          if (img) {
            img.style.opacity = '1';
            img.style.visibility = 'visible';
          }
        }
      }
      
      // Update thumbnails
      this.updateThumbnails();

      // Update zoom button visibility
      if (this.zoomButton && this.enableZoom && this.mediaItems[this.currentIndex]) {
        const mediaType = this.mediaItems[this.currentIndex].getAttribute('data-media-type');
        this.zoomButton.hidden = mediaType !== 'image';
      }
    });
  }
  
  updateThumbnails() {
    if (!this.thumbnailList) return;
    
    // Immediate thumbnail updates for better responsiveness
    const thumbnails = this.thumbnailList.querySelectorAll('.product__thumbnail');
    
    // Batch DOM updates for better performance
    thumbnails.forEach((thumb) => {
      const mediaId = thumb.getAttribute('data-media-id');
      const currentMediaId = this.mediaItems[this.currentIndex].getAttribute('data-media-id');
      const isActive = mediaId === currentMediaId;
      
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
    
    // Scroll active thumbnail into view with instant scroll for immediate feedback
    const currentThumbnail = this.thumbnailList.querySelector('.product__thumbnail.is-active');
    if (currentThumbnail && currentThumbnail.scrollIntoView) {
      // Use instant scroll for mobile, smooth for desktop
      const scrollBehavior = this.isMobile ? 'auto' : 'smooth';
      currentThumbnail.scrollIntoView({
        behavior: scrollBehavior,
        block: 'nearest',
        inline: 'center'
      });
    }
  }
  
  // Optimized mobile scroll handler
  handleMobileScroll() {
    if (!this.isMobile || !this.mediaList) return;
    
    // Use Intersection Observer if supported
    if (!this.intersectionObserver && 'IntersectionObserver' in window) {
      this.setupIntersectionObserver();
      return;
    }
    
    // Fallback to scroll calculation
    this.calculateVisibleItem();
  }
  
  setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      let mostVisible = { element: null, ratio: 0, index: 0 };
      
      entries.forEach((entry) => {
        if (entry.intersectionRatio > mostVisible.ratio) {
          mostVisible.ratio = entry.intersectionRatio;
          mostVisible.element = entry.target;
          mostVisible.index = this.mediaItems.indexOf(entry.target);
        }
      });
      
      if (mostVisible.index !== -1 && mostVisible.index !== this.currentIndex) {
        this.currentIndex = mostVisible.index;
        this.updateThumbnails();
        this.dispatchCarouselEvent();
        // Preload asynchronously
        requestIdleCallback(() => {
          this.preloadAdjacentImages();
        }, { timeout: 500 });
      }
    }, {
      root: this.mediaList,
      threshold: [0.5, 0.9]
    });
    
    // Watch all images for visibility
    this.mediaItems.forEach(item => {
      this.intersectionObserver.observe(item);
    });
  }
  
  calculateVisibleItem() {
    // For mobile with scroll-snap: calculate based on scroll position
    if (this.isMobile && this.mediaList) {
      const scrollLeft = this.mediaList.scrollLeft;
      const itemWidth = this.mediaList.offsetWidth;
      const calculatedIndex = Math.round(scrollLeft / itemWidth);
      const clampedIndex = Math.max(0, Math.min(calculatedIndex, this.mediaItems.length - 1));
      
      if (clampedIndex !== this.currentIndex) {
        this.currentIndex = clampedIndex;
        this.updateThumbnails();
        this.dispatchCarouselEvent();
        // Preload next 5 images immediately to avoid blocking on rapid scroll
        if (window.requestIdleCallback) {
          requestIdleCallback(() => {
            this.preloadAdjacentImages();
          }, { timeout: 100 });
        } else {
          setTimeout(() => {
            this.preloadAdjacentImages();
          }, 0);
        }
      }
      return;
    }
    
    // Fallback: find most visible image manually for desktop/older browsers
    let bestVisibleIndex = 0;
    let maxVisibleWidth = 0;
    this.mediaItems.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const containerRect = this.mediaList.getBoundingClientRect();
      const leftEdge = Math.max(rect.left, containerRect.left);
      const rightEdge = Math.min(rect.right, containerRect.right);
      const visibleWidth = Math.max(0, rightEdge - leftEdge);
      if (visibleWidth > maxVisibleWidth) {
        maxVisibleWidth = visibleWidth;
        bestVisibleIndex = index;
      }
    });
    if (bestVisibleIndex !== this.currentIndex) {
      this.currentIndex = bestVisibleIndex;
      this.updateThumbnails();
      this.dispatchCarouselEvent();
      // Preload next 5 images
      if (window.requestIdleCallback) {
        requestIdleCallback(() => {
          this.preloadAdjacentImages();
        }, { timeout: 100 });
      } else {
        setTimeout(() => {
          this.preloadAdjacentImages();
        }, 0);
      }
    }
  }
  
  dispatchCarouselEvent() {
    const event = new CustomEvent('carousel:changed', { 
      detail: { index: this.currentIndex } 
    });
    this.container.dispatchEvent(event);
  }

  createThumbnails() {
    if (!this.thumbnailList) return;
    
    // Add click events to thumbnails rendered by theme
    const thumbnails = this.thumbnailList.querySelectorAll('.product__thumbnail');
    
    thumbnails.forEach((thumbnail) => {
      // Extract position from the data attribute
      const position = parseInt(thumbnail.getAttribute('data-position'), 10);
      
      // Add click event listener
      thumbnail.addEventListener('click', () => {
        this.goToIndex(position);
      });
    });
  }

  setupEventListeners() {
    // Connect navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.goToPrev.bind(this));
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.goToNext.bind(this));
    }
    
    // Listen for product variant changes
    this.setupVariantChangeListener();
    
    // Throttled resize handler
    this.onResize = this.throttledResize;
    window.addEventListener('resize', this.onResize, { passive: true });
    
    // Keyboard navigation for arrows
    const arrowButtons = this.container.querySelectorAll('.product__media-arrow');
    arrowButtons.forEach(button => {
      button.addEventListener('keydown', this.handleArrowKeydown.bind(this), { passive: false });
    });
    
    // Global keyboard navigation
    document.addEventListener('keydown', this.handleKeyboardNavigation.bind(this), { passive: false });
    
    // Touch and swipe navigation
    this.setupTouchNavigation();
  }
  
  handleArrowKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.target.click();
    }
  }
  
  handleKeyboardNavigation(e) {
    if (this.isZoomed) {
      if (e.key === 'Escape') this.closeZoom();
      if (e.key === 'ArrowLeft') this.zoomGoPrev();
      if (e.key === 'ArrowRight') this.zoomGoNext();
      return;
    }
    
    if (this.container.contains(document.activeElement) || this.container.matches(':focus-within')) {
      if (e.key === 'ArrowLeft') this.goToPrev();
      if (e.key === 'ArrowRight') this.goToNext();
    }
  }
  
  setupTouchNavigation() {
    if (this.isMobile) {
      // For mobile with scroll-snap, use scrollend event for better sync
      if ('onscrollend' in window) {
        this.mediaList.addEventListener('scrollend', () => {
          this.calculateVisibleItem();
        }, { passive: true });
      } else {
        // Fallback: throttled scroll events for older browsers
        let scrollTimeout;
        this.mediaList.addEventListener('scroll', () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            this.calculateVisibleItem();
          }, 100);
        }, { passive: true });
      }
    } else {
      // For desktop, use touch events for swiping
      let startX = 0;
      
      this.mediaList.addEventListener('touchstart', (e) => {
        startX = e.changedTouches[0].screenX;
      }, { passive: true });
      
      this.mediaList.addEventListener('touchend', (e) => {
        const endX = e.changedTouches[0].screenX;
        const threshold = 50;
        
        if (startX - endX > threshold) {
          this.goToNext();
        } else if (endX - startX > threshold) {
          this.goToPrev();
        }
      }, { passive: true });
    }
  }
  
  // Preload a specific image by index
  preloadSpecificImage(index) {
    const mediaItem = this.mediaItems[index];
    if (!mediaItem) return;
    
    const img = mediaItem.querySelector('img');
    if (!img) return;
    
    // Make it visible and force load
    mediaItem.style.visibility = 'visible';
    mediaItem.style.opacity = '1';
    
    img.loading = 'eager';
    img.removeAttribute('loading');
    img.style.opacity = '1';
    img.style.visibility = 'visible';
    
    // Force with decode
    if (img.decode && !img.complete) {
      img.decode().catch(() => {});
    }
    
    // Force with new Image
    if (!img.complete) {
      const forceImg = new Image();
      forceImg.src = img.currentSrc || img.src;
      if (img.srcset) {
        forceImg.srcset = img.srcset;
      }
    }
  }
  
  // Preload images before and after current one
  preloadAdjacentImages() {
    // Preload next 5 images for smoother rapid scrolling
    const indicesToPreload = [
      (this.currentIndex + 1) % this.mediaItems.length,
      (this.currentIndex + 2) % this.mediaItems.length,
      (this.currentIndex + 3) % this.mediaItems.length,
      (this.currentIndex + 4) % this.mediaItems.length,
      (this.currentIndex + 5) % this.mediaItems.length
    ];
    
    indicesToPreload.forEach(index => {
      const mediaItem = this.mediaItems[index];
      if (!mediaItem) return;
      
      const img = mediaItem.querySelector('img');
      if (!img) return;
      
      // Force immediate loading
      img.loading = 'eager';
      img.removeAttribute('loading');
      
      // Force decode and display
      if (img.complete) {
        img.style.opacity = '1';
        img.style.visibility = 'visible';
      } else {
        // Force loading by creating a new image request
        const tempImg = new Image();
        tempImg.onload = () => {
          img.style.opacity = '1';
          img.style.visibility = 'visible';
        };
        tempImg.src = img.currentSrc || img.src;
        if (img.srcset) {
          tempImg.srcset = img.srcset;
        }
      }
    });
  }

  goToPrev() {
    if (this.mediaItems.length <= 1) return;
    
    const newIndex = this.currentIndex === 0 
      ? this.mediaItems.length - 1 
      : this.currentIndex - 1;
    
    this.goToIndex(newIndex);
  }

  goToNext() {
    if (this.mediaItems.length <= 1) return;
    
    const newIndex = this.currentIndex === this.mediaItems.length - 1 
      ? 0 
      : this.currentIndex + 1;
    
    this.goToIndex(newIndex);
  }

  goToIndex(index) {
    if (index === this.currentIndex || index < 0 || index >= this.mediaItems.length) return;
    
    this.currentIndex = index;
    
    // Batch all updates in a single requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      this.updateCarousel();
      this.ensureImageVisibility();
      // Preload adjacent images when navigating
      this.preloadAdjacentImages();
    });
  }

  setupZoom() {
    if (!this.zoomButton) return;
    
    this.zoomButton.addEventListener('click', () => {
      this.openZoom();
    });
  }

  openZoom() {
    // Create zoom overlay if it doesn't exist
    if (!this.zoomOverlay) {
      this.zoomOverlay = document.createElement('div');
      this.zoomOverlay.className = 'product-zoom-overlay';
      this.zoomOverlay.setAttribute('role', 'dialog');
      this.zoomOverlay.setAttribute('aria-modal', 'true');
      this.zoomOverlay.setAttribute('aria-label', 'Image zoom view');
      document.body.appendChild(this.zoomOverlay);
      
      // Add close button
      const closeButton = document.createElement('button');
      closeButton.className = 'product-zoom-close';
      closeButton.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/></svg>';
      closeButton.setAttribute('aria-label', 'Close zoom view');
      closeButton.addEventListener('click', this.closeZoom.bind(this));
      this.zoomOverlay.appendChild(closeButton);
      
      // Add navigation buttons
      const prevButton = document.createElement('button');
      prevButton.className = 'product-zoom-nav product-zoom-prev';
      prevButton.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" fill="currentColor"/></svg>';
      prevButton.setAttribute('aria-label', 'Previous image');
      prevButton.addEventListener('click', this.zoomGoPrev.bind(this));
      this.zoomOverlay.appendChild(prevButton);
      
      const nextButton = document.createElement('button');
      nextButton.className = 'product-zoom-nav product-zoom-next';
      nextButton.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor"/></svg>';
      nextButton.setAttribute('aria-label', 'Next image');
      nextButton.addEventListener('click', this.zoomGoNext.bind(this));
      this.zoomOverlay.appendChild(nextButton);
      
      // Add container for zoom images
      this.zoomContainer = document.createElement('div');
      this.zoomContainer.className = 'product-zoom-container';
      this.zoomOverlay.appendChild(this.zoomContainer);
      
      // Setup focus trap for accessibility
      this.zoomOverlay.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          const focusableElements = this.zoomOverlay.querySelectorAll('button, [tabindex]:not([tabindex="-1"])');
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];
          
          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              lastElement.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastElement) {
              firstElement.focus();
              e.preventDefault();
            }
          }
        }
      });
    }
    
    // Clear existing zoom images
    this.zoomContainer.innerHTML = '';
    
    // Get large images for each media item
    const imagesToLoad = [];
    
    this.mediaItems.forEach((mediaItem, index) => {
      if (mediaItem.getAttribute('data-media-type') === 'image') {
        const image = mediaItem.querySelector('img');
        if (image) {
          // Extract large image URL
          const smallSrc = image.src;
          const largeSrc = this.getLargeImageUrl(smallSrc);
          
          // Create zoom image container
          const zoomImageContainer = document.createElement('div');
          zoomImageContainer.className = 'product-zoom-item';
          zoomImageContainer.style.display = index === this.currentIndex ? 'flex' : 'none';
          
          // Add loading indicator
          const loader = document.createElement('div');
          loader.className = 'product-zoom-loader';
          zoomImageContainer.appendChild(loader);
          
          // Create image element
          const zoomImage = document.createElement('img');
          zoomImage.className = 'product-zoom-image';
          zoomImage.alt = image.alt || '';
          
          // Setup pan/zoom behavior
          let isPanning = false;
          let startX = 0;
          let startY = 0;
          let translateX = 0;
          let translateY = 0;
          let scale = 1;
          
          zoomImage.addEventListener('load', () => {
            loader.style.display = 'none';
          });
          
          zoomImage.addEventListener('mousedown', (e) => {
            if (scale > 1) {
              isPanning = true;
              startX = e.clientX - translateX;
              startY = e.clientY - translateY;
              zoomImage.style.cursor = 'grabbing';
              e.preventDefault();
            }
          });
          
          window.addEventListener('mousemove', (e) => {
            if (!isPanning) return;
            
            translateX = e.clientX - startX;
            translateY = e.clientY - startY;
            
            // Limit pan to image boundaries
            const maxTranslateX = (scale - 1) * zoomImage.width / 2;
            const maxTranslateY = (scale - 1) * zoomImage.height / 2;
            
            translateX = Math.min(Math.max(translateX, -maxTranslateX), maxTranslateX);
            translateY = Math.min(Math.max(translateY, -maxTranslateY), maxTranslateY);
            
            zoomImage.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
          });
          
          window.addEventListener('mouseup', () => {
            isPanning = false;
            zoomImage.style.cursor = scale > 1 ? 'grab' : 'default';
          });
          
          // Double click to zoom in/out
          zoomImageContainer.addEventListener('dblclick', (e) => {
            if (scale === 1) {
              scale = 2;
              // Zoom to cursor position
              const rect = zoomImage.getBoundingClientRect();
              const offsetX = (e.clientX - rect.left) / rect.width;
              const offsetY = (e.clientY - rect.top) / rect.height;
              
              translateX = (rect.width / 2 - offsetX * rect.width) * (scale - 1);
              translateY = (rect.height / 2 - offsetY * rect.height) * (scale - 1);
              
              zoomImage.style.cursor = 'grab';
            } else {
              scale = 1;
              translateX = 0;
              translateY = 0;
              zoomImage.style.cursor = 'default';
            }
            
            zoomImage.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
          });
          
          zoomImage.src = largeSrc;
          zoomImageContainer.appendChild(zoomImage);
          this.zoomContainer.appendChild(zoomImageContainer);
          
          imagesToLoad.push({ src: largeSrc, index });
        }
      }
    });
    
    // Show overlay
    this.zoomOverlay.classList.add('visible');
    document.body.classList.add('product-zoom-open');
    this.isZoomed = true;
    
    // Preload adjacent images
    this.preloadZoomImages(imagesToLoad);
  }

  closeZoom() {
    if (this.zoomOverlay) {
      this.zoomOverlay.classList.remove('visible');
      document.body.classList.remove('product-zoom-open');
      this.isZoomed = false;
      
      // Return focus to the zoom button for better accessibility
      if (this.zoomButton) {
        setTimeout(() => {
          this.zoomButton.focus();
        }, 100);
      }
    }
  }

  zoomGoPrev() {
    if (!this.isZoomed) return;
    
    let newIndex = this.currentIndex;
    
    // Find previous image
    do {
      newIndex = newIndex === 0 ? this.mediaItems.length - 1 : newIndex - 1;
      if (this.mediaItems[newIndex].getAttribute('data-media-type') === 'image') break;
    } while (newIndex !== this.currentIndex);
    
    if (newIndex !== this.currentIndex) {
      this.currentIndex = newIndex;
      this.updateZoomView();
      
      // Reset zoom level when changing images
      const zoomItems = this.zoomContainer.querySelectorAll('.product-zoom-item');
      zoomItems.forEach(item => {
        const img = item.querySelector('img');
        if (img) {
          img.style.transform = 'translate(0, 0) scale(1)';
        }
      });
    }
  }

  zoomGoNext() {
    if (!this.isZoomed) return;
    
    let newIndex = this.currentIndex;
    
    // Find next image
    do {
      newIndex = newIndex === this.mediaItems.length - 1 ? 0 : newIndex + 1;
      if (this.mediaItems[newIndex].getAttribute('data-media-type') === 'image') break;
    } while (newIndex !== this.currentIndex);
    
    if (newIndex !== this.currentIndex) {
      this.currentIndex = newIndex;
      this.updateZoomView();
      
      // Reset zoom level when changing images
      const zoomItems = this.zoomContainer.querySelectorAll('.product-zoom-item');
      zoomItems.forEach(item => {
        const img = item.querySelector('img');
        if (img) {
          img.style.transform = 'translate(0, 0) scale(1)';
        }
      });
    }
  }

  updateZoomView() {
    const zoomItems = this.zoomContainer.querySelectorAll('.product-zoom-item');
    
    zoomItems.forEach((item, index) => {
      item.style.display = index === this.currentIndex ? 'flex' : 'none';
    });
    
    // Update main carousel
    this.updateCarousel();
  }

  getLargeImageUrl(url) {
    // Convert Shopify image URL to large size
    // Example: cdn.shopify.com/s/files/1/0xxx/0xxx/products/image_100x100.jpg -> cdn.shopify.com/s/files/1/0xxx/0xxx/products/image_2048x2048.jpg
    return url.replace(/(_\d+x\d+)?(\.[a-zA-Z]{3,4})$/, '_2048x2048$2');
  }

  // Use a smaller, optimized size for thumbnails (good quality for small UI; retina-friendly when browser upscales)
  getThumbnailImageUrl(url) {
    if (!url) return '';
    const [base, query] = url.split('?');
    const sized = base.replace(/(_\d+x\d+)?(\.[a-zA-Z]{3,4})$/, '_240x$2');
    return query ? `${sized}?${query}` : sized;
  }
  
  preloadZoomImages(imagesToLoad) {
    // Preload current image first, then adjacent images
    imagesToLoad.sort((a, b) => {
      const distA = Math.abs(a.index - this.currentIndex);
      const distB = Math.abs(b.index - this.currentIndex);
      return distA - distB;
    });
    
    let loadIndex = 0;
    
    const loadNext = () => {
      if (loadIndex >= imagesToLoad.length) return;
      
      const img = new Image();
      img.onload = () => {
        loadIndex++;
        if (loadIndex < imagesToLoad.length) {
          loadNext();
        }
      };
      
      img.src = imagesToLoad[loadIndex].src;
    };
    
    loadNext();
  }
  
  setupVariantChangeListener() {
    // Listen for variant changes
    document.addEventListener('variant:changed', (event) => {
      if (!event.detail.variant || !event.detail.variant.featured_media) return;
      
      // Find the media item that matches the variant's featured media
      const variantMediaId = event.detail.variant.featured_media.id;
      
      // Find the index of the media item with this ID
      const mediaIndex = this.findMediaIndexById(variantMediaId);
      
      if (mediaIndex !== -1 && mediaIndex !== this.currentIndex) {
        // Navigate to the variant's media
        this.goToIndex(mediaIndex);
        
        // Make sure thumbnail is properly focused and scrolled into view
        this.updateThumbnails();
        
        // Ensure image visibility after a short delay
        setTimeout(() => {
          this.ensureImageVisibility();
        }, 50);
      }
    });
  }
  
  findMediaIndexById(mediaId) {
    // Convert to string for comparison since attributes are strings
    mediaId = String(mediaId);
    
    // Look through all media items for the one with matching ID
    for (let i = 0; i < this.mediaItems.length; i++) {
      const itemMediaId = this.mediaItems[i].getAttribute('data-media-id');
      if (itemMediaId === mediaId) {
        return i;
      }
    }
    
    return -1;
  }
  
  handleResize() {
    // Check if mobile state has changed
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 749;
    
    // Only update if there was an actual change
    if (wasMobile !== this.isMobile) {
      this.updateLayoutForScreenSize();
    }
  }
  
  updateLayoutForScreenSize() {
    // Clean up previous observers
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    
    // Update touch navigation
    this.setupTouchNavigation();
    
    // Update carousel layout
    requestAnimationFrame(() => {
      if (this.isMobile && this.mediaList) {
        this.mediaList.style.display = 'flex';
        this.mediaList.style.overflowX = 'auto';
        
        this.mediaItems.forEach(item => {
          item.style.flex = '0 0 100%';
          item.style.width = '100%';
          item.style.scrollSnapAlign = 'center';
        });
      } else if (this.mediaList) {
        this.mediaList.style.display = '';
        this.mediaList.style.overflowX = '';
        
        this.mediaItems.forEach(item => {
          item.style.flex = '';
          item.style.width = '';
          item.style.scrollSnapAlign = '';
        });
      }
      
      this.updateCarousel();
    });
  }
}

// Initialize when DOM is loaded 
document.addEventListener('DOMContentLoaded', function() {
  const productMediaContainers = document.querySelectorAll('product-media');
  
  productMediaContainers.forEach(container => {
    const enableZoom = container.hasAttribute('data-enable-zoom') || container.hasAttribute('enable-zoom-image');
    const thumbnailsPosition = container.getAttribute('thumbnails-position') || 'bottom';
    const productHandle = container.getAttribute('product-handle');
    const initialVariantId = container.getAttribute('initial-variant-id') || null;
    
    // Initialize carousel
    try {
      new ProductCarousel({
        container,
        productHandle,
        enableZoom,
        thumbnailsPosition,
        initialVariantId
      });
    } catch (error) {
      console.warn('Failed to initialize carousel:', error);
    }
  });
});
